import { Period } from "hooks/periodIndex";
import { PlanId } from "components/common/boundary/PlanId";
import {
  ChefGroupChargeScheduleOverride,
  MixMetadata,
  PeriodSummary,
  PublishPlanCreate,
} from "src/store/api/generatedApi";
import {
  makePlanPeriodId,
  PlanPeriodId,
  PlanMixTableEdits,
} from "src/store/slices/planMixesTableEditSlice";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { typeSafeObjectEntries } from "src/utils/typeSafeObjectEntries";
import { Row } from "components/common/plan/types";

const UNKNOWN_CHEF_GROUP_NAME = "UNKNOWN";

type MixMetadataWithOptionalChefGroupName = Omit<
  MixMetadata,
  "chef_group_name"
> &
  Partial<Pick<MixMetadata, "chef_group_name">>;

export const getMixIdsRequiringMixCodeOverride = (
  mixMetadata: MixMetadataWithOptionalChefGroupName[],
  period: Period,
  mixCodeOverrideOptions: string[] | undefined
): number[] =>
  !mixCodeOverrideOptions?.length
    ? []
    : mixMetadata
        .filter(
          (metadata) =>
            metadata.period === period &&
            (metadata.chef_group_name ?? "") === UNKNOWN_CHEF_GROUP_NAME
        )
        .map(({ mix_id }) => mix_id);

export const hasMissingMixCodeOverrides = ({
  checkedPlans,
  formPlanId,
  manualHeatOverrides,
  mixMetadata,
  mixCodeOverrideOptions,
}: {
  checkedPlans: { planId: PlanId; period: Period }[];
  formPlanId: PlanId;
  manualHeatOverrides: Record<Period, Record<number, string>>;
  mixMetadata: MixMetadata[];
  mixCodeOverrideOptions: string[] | undefined;
}): boolean =>
  checkedPlans.some(
    ({ planId, period }) =>
      planId === formPlanId &&
      getMixIdsRequiringMixCodeOverride(
        mixMetadata,
        period,
        mixCodeOverrideOptions
      ).some(
        (mixId) =>
          !mixCodeOverrideOptions?.includes(
            manualHeatOverrides[period]?.[mixId] ?? ""
          )
      )
  );

export const getPlanPeriodKeys = (
  periods: PeriodSummary[],
  allBackupPlanIds: PlanId[],
  planId: PlanId
): PlanPeriodId[] => {
  return [
    ...periods
      .filter((period) => period.is_deployable)
      .map((period) => makePlanPeriodId(planId, period.period as Period)),
    ...allBackupPlanIds.map((planId) => makePlanPeriodId(planId, 1 as Period)),
  ];
};

export const filterPeriodPlanEdits = (
  periodPlanEdits: Record<
    PlanPeriodId,
    | {
        planId: PlanId;
        period: Period;
        editedRows: Row[];
        originalRows: Row[];
        isSubmitting: boolean;
        comment: string;
      }
    | undefined
  >
): Record<PlanPeriodId, PlanMixTableEdits> => {
  return typeSafeObjectFromEntries(
    typeSafeObjectEntries(periodPlanEdits).filter(
      (arg): arg is [PlanPeriodId, PlanMixTableEdits] => arg[1] !== undefined
    )
  );
};

export const buildPublishPlans = ({
  checkedPlans,
  formPlanId,
  manualHeatOverrides,
  mixMetadata,
}: {
  checkedPlans: { planId: PlanId; period: Period }[];
  formPlanId: PlanId;
  manualHeatOverrides: Record<Period, Record<number, string>>;
  mixMetadata: MixMetadata[];
}): PublishPlanCreate[] => {
  return checkedPlans.map(({ planId, period }): PublishPlanCreate => {
    if (planId !== formPlanId) {
      return { plan_id: planId, period };
    }
    const periodOverrides = manualHeatOverrides[period] ?? {};
    const chefGroupOverrides = mixMetadata
      .filter((meta) => meta.period === period)
      .map((meta): ChefGroupChargeScheduleOverride | null => {
        const override = periodOverrides[meta.mix_id];
        return override === undefined || override === ""
          ? null
          : {
              chef_group_id: meta.chef_group_id,
              manual_heat_override: override,
            };
      })
      .filter(
        (
          chefGroupOverride
        ): chefGroupOverride is ChefGroupChargeScheduleOverride =>
          chefGroupOverride !== null
      );
    return {
      plan_id: planId,
      period,
      chef_group_overrides: chefGroupOverrides,
    };
  });
};

export const unpackEdits = (
  periodPlanEdits: Record<PlanPeriodId, PlanMixTableEdits>
): {
  isSubmitting: boolean;
  originalRows: Record<PlanPeriodId, Row[]>;
  editedRows: Record<PlanPeriodId, Row[]>;
} => {
  return {
    isSubmitting: Object.values(periodPlanEdits).some(
      ({ isSubmitting }) => isSubmitting
    ),
    originalRows: typeSafeObjectFromEntries(
      typeSafeObjectEntries(periodPlanEdits).map(
        ([planPeriodId, { originalRows }]) => [planPeriodId, originalRows]
      )
    ),
    editedRows: typeSafeObjectFromEntries(
      typeSafeObjectEntries(periodPlanEdits).map(
        ([planPeriodId, { editedRows }]) => [planPeriodId, editedRows]
      )
    ),
  };
};
